import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { PenTool, Trash2, RefreshCw, AlertCircle, Users } from 'lucide-react';
import { beesClient } from '../../api/beesClient.js';
import { usePresence } from '../../hooks/usePresence.js';

interface Annotation {
  id: string;
  content: string;
  x: number;
  y: number;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

const AnnotationPanel: React.FC = () => {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [currentUser] = useState({ id: 'user_' + Math.random().toString(36).substr(2, 9), name: 'User' });

  const presenceState = usePresence({
    url: import.meta.env.VITE_WS_URL ? `${import.meta.env.VITE_WS_URL}/presence` : 'wss://hivecore-backend-production.up.railway.app/presence',
    userId: currentUser.id,
    userName: currentUser.name,
    userColor: '#10B981',
    onUserJoin: (user) => {
      console.log('User joined annotation canvas:', user.name);
    },
    onUserLeave: (userId) => {
      console.log('User left annotation canvas:', userId);
    },
    onCursorMove: (userId, cursor) => {
      console.log('User cursor moved:', userId, cursor);
    }
  });

  const fetchAnnotations = React.useCallback(async () => {
    try {
      setError(null);
      const response = await beesClient.runAnnotation({
        action: 'list',
        userId: currentUser.id
      });
      
      if (response.success && response.data) {
        setAnnotations(Array.isArray(response.data) ? response.data : []);
      } else {
        setError('Failed to load annotations');
        setAnnotations([]);
      }
    } catch (err) {
      console.error('Failed to fetch annotations:', err);
      setError('Failed to load annotations');
      setAnnotations([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser.id]);

  const createAnnotation = async (x: number, y: number, content: string) => {
    try {
      setError(null);
      const response = await beesClient.runAnnotation({
        action: 'create',
        userId: currentUser.id,
        content,
        x,
        y
      });
      
      if (response.success && response.data) {
        setAnnotations(prev => [...prev, response.data as Annotation]);
      } else {
        setError(`Failed to create annotation: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to create annotation:', err);
      setError('Failed to create annotation');
    }
  };

  const deleteAnnotation = async (id: string) => {
    try {
      setError(null);
      const response = await beesClient.runAnnotation({
        action: 'delete',
        userId: currentUser.id,
        id
      });
      
      if (response.success) {
        setAnnotations(prev => prev.filter(ann => ann.id !== id));
      } else {
        setError(`Failed to delete annotation: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to delete annotation:', err);
      setError('Failed to delete annotation');
    }
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isCreating || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const content = prompt('Enter annotation text:');
    if (content && content.trim()) {
      createAnnotation(x, y, content.trim());
    }
    setIsCreating(false);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    presenceState.updateCursor(x, y);
  };

  useEffect(() => {
    fetchAnnotations();
  }, [fetchAnnotations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-900 text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <div className="flex items-center space-x-2">
          <PenTool className="w-5 h-5 text-green-400" />
          <h2 className="text-lg font-semibold">Annotation Canvas</h2>
          <Users className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-400">{annotations.length} annotations</span>
          <div className={`w-2 h-2 rounded-full ${presenceState.isConnected ? 'bg-green-400' : 'bg-red-400'}`} />
          <span className="text-xs text-gray-400">
            {presenceState.users.size} user{presenceState.users.size !== 1 ? 's' : ''} online
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsCreating(!isCreating)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              isCreating 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {isCreating ? 'Cancel' : 'Add Annotation'}
          </button>
          
          <button
            onClick={fetchAnnotations}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            title="Refresh annotations"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-4 mt-4 p-3 bg-red-900/50 border border-red-700 rounded-lg flex items-center space-x-2"
        >
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </motion.div>
      )}

      {/* Instructions */}
      {isCreating && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-4 mt-4 p-3 bg-green-900/30 border border-green-700 rounded-lg"
        >
          <p className="text-green-300 text-sm">
            Click anywhere on the canvas below to create a new annotation
          </p>
        </motion.div>
      )}

      {/* Canvas Area */}
      <div className="flex-1 relative overflow-hidden">
        <div
          ref={canvasRef}
          className={`w-full h-full relative bg-gray-800 ${
            isCreating ? 'cursor-crosshair' : 'cursor-default'
          }`}
          onClick={handleCanvasClick}
          onMouseMove={handleMouseMove}
        >
          {/* Grid Background */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px'
            }}
          />

          {/* Annotations */}
          {annotations.map((annotation) => (
            <motion.div
              key={annotation.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute group"
              style={{
                left: annotation.x,
                top: annotation.y,
                transform: 'translate(-50%, -50%)'
              }}
            >
              {/* Annotation Marker */}
              <div className="relative">
                <div className="w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-lg"></div>
                
                {/* Annotation Content */}
                <div className="absolute left-6 top-0 min-w-48 max-w-64 bg-gray-900 border border-gray-600 rounded-lg p-3 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <div className="text-sm text-white mb-2">{annotation.content}</div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>
                      {new Date(annotation.createdAt).toLocaleString()}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteAnnotation(annotation.id);
                      }}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      title="Delete annotation"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Other Users' Cursors */}
          {Array.from(presenceState.users.values()).map((user) => (
            <motion.div
              key={user.id}
              className="absolute pointer-events-none z-20"
              style={{
                left: user.cursor.x,
                top: user.cursor.y,
                transform: 'translate(-50%, -50%)'
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <div 
                className="w-3 h-3 rounded-full border-2 border-white shadow-lg"
                style={{ backgroundColor: user.color }}
              />
              <div 
                className="absolute left-4 top-0 px-2 py-1 text-xs text-white rounded shadow-lg whitespace-nowrap"
                style={{ backgroundColor: user.color }}
              >
                {user.name}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Footer Stats */}
      <div className="p-4 border-t border-gray-700 bg-gray-800/50">
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>
            {annotations.length} annotation{annotations.length !== 1 ? 's' : ''} total
          </span>
          <span>
            User: {currentUser.name}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AnnotationPanel;
